import type { Dictionary } from '@/i18n/dictionary'
import { mediusTermsEn } from '@/i18n/medius-terms'
import { mediusDeletionEn, mediusPrivacyEn } from '@/i18n/medius-policies'
import { sparsaAcceptableUseEn, sparsaTermsEn } from '@/i18n/sparsa-terms'
import { sparsaDeletionEn, sparsaPrivacyEn } from '@/i18n/sparsa-policies'

export const de: Dictionary = {
  meta: {
    home: {
      title: 'Dalmos Systems — Unternehmenssoftware nach Maß, CRM und Integration',
      description:
        'Dalmos Systems entwickelt individuelle Unternehmenssoftware für Finanzen, Immobilien, Hospitality und Logistik — CRM-, ERP- und PMS-Integrationen, Cloud-Infrastruktur und langfristige Wartung.',
      keywords: [
        'individuelle Unternehmenssoftware',
        'CRM-Einführung',
        'Immobiliensoftware',
        'Hotel- und Hospitality-Software',
        'ERP- und PMS-Integrationen',
        'API-Integrationen',
        'Microsoft Dynamics 365',
        'Power Platform',
        'Softwareentwicklung Kroatien',
      ],
    },
    privacy: {
      title: 'Datenschutzerklärung — Dalmos Systems',
      description:
        'Wie Dalmos Systems personenbezogene Daten aus dem Kontaktformular gemäß DSGVO verarbeitet.',
    },
    legal: {
      title: 'Impressum — Dalmos Systems',
      description:
        'Rechtliche Angaben zu Dalmos Systems: eingetragener Name, Sitz in Šibenik, Eintrag im kroatischen Handwerksregister und Kontaktdaten.',
    },
    mediusPrivacy: {
      title: 'Medius – Datenschutzerklärung (Englisch) — Dalmos Systems',
      description:
        'Wie Medius von Dalmos Systems personenbezogene Daten im Auftrag seiner Kunden verarbeitet. Die Erklärung gibt es auf Englisch und Kroatisch.',
    },
    mediusDeletion: {
      title: 'Medius – Datenlöschung (Englisch) — Dalmos Systems',
      description:
        'So beantragen Sie die Löschung Ihrer personenbezogenen Daten aus Medius, auch von Nachrichten über Facebook Messenger, Instagram und WhatsApp.',
    },
    mediusTerms: {
      title: 'Medius – Nutzungsbedingungen (Englisch) — Dalmos Systems',
      description:
        'Die Bedingungen, zu denen Dalmos Systems Medius für Geschäftskunden bereitstellt. Die Bedingungen gibt es auf Englisch und Kroatisch.',
    },
    sparsaPrivacy: {
      title: 'Sparsa – Datenschutzhinweise (Englisch) — Dalmos Systems',
      description:
        'Wie Sparsa von Dalmos Systems personenbezogene Daten von Newsletter-Empfängern verarbeitet. Die Hinweise gibt es auf Englisch und Kroatisch.',
    },
    sparsaDeletion: {
      title: 'Sparsa – Datenlöschung (Englisch) — Dalmos Systems',
      description:
        'So bestellen Sie Newsletter ab, die über Sparsa versendet werden, und beantragen die Löschung Ihrer personenbezogenen Daten.',
    },
    sparsaTerms: {
      title: 'Sparsa – Nutzungsbedingungen (Englisch) — Dalmos Systems',
      description:
        'Die Bedingungen, zu denen Dalmos Systems den Newsletter-Dienst Sparsa für Unternehmen bereitstellt. Die Bedingungen gibt es auf Englisch und Kroatisch.',
    },
    sparsaAcceptableUse: {
      title: 'Sparsa – Nutzungsrichtlinie (Englisch) — Dalmos Systems',
      description:
        'Die Regeln für den Versand mit Sparsa: Einwilligung jedes Empfängers, keine gekauften Listen, Bestätigung bei Importen und Meldung von Missbrauch.',
    },
    notFound: {
      title: 'Seite nicht gefunden — Dalmos Systems',
      description: 'Die angeforderte Seite konnte nicht gefunden werden.',
    },
  },
  nav: {
    home: 'Start',
    services: 'Leistungen',
    about: 'Über uns',
    contact: 'Kontakt',
    cta: 'Angebot anfordern',
    openMenu: 'Navigation öffnen',
    closeMenu: 'Navigation schließen',
    languageLabel: 'Sprache wählen',
    currentLanguage: 'Aktuelle Sprache',
  },
  a11y: {
    skipToContent: 'Zum Inhalt springen',
    decorative: 'Dekoratives Element',
  },
  hero: {
    eyebrow: 'Individuelle Softwarelösungen für Unternehmen',
    headlineBefore: 'Systeme, die für Ihr Unternehmen arbeiten,',
    headlineAccent: 'nicht umgekehrt.',
    subtitle:
      'Ein technischer Partner für individuelle Unternehmenssoftware — Lösungen für Finanzen, Immobilien, Hospitality und Logistik, aufgebaut auf Erfahrung aus großen CRM-Projekten, mit dem Microsoft-Ökosystem als verlässlicher Grundlage und nicht als einzigem Fokus.',
    ctaPrimary: 'Angebot anfordern',
    ctaSecondary: 'Leistungen ansehen',
    chips: [
      'CRM- und ERP-Integrationen',
      'Hospitality und PMS',
      'Finanzlösungen',
      'Immobilien',
      'Mobile Apps',
      'Lager- und Gebäudemanagement',
    ],
    scrollHint: 'Scrollen',
  },
  stats: {
    title: 'Worauf wir aufbauen',
    items: [
      { value: '20+', label: 'Leistungsbereiche unter einem Dach' },
      { value: '6', label: 'Branchen mit erprobten Lösungsmustern' },
      { value: '1', label: 'Person, die das Projekt führt und den Code schreibt' },
      { value: '100 %', label: 'Der Projekte gehen mit definiertem Support live' },
    ],
  },
  why: {
    title: 'Warum Dalmos Systems',
    cards: [
      {
        title: 'Direkter Zugang',
        body: 'Keine Agentur dazwischen — Sie sprechen direkt mit der Person, die das Projekt umsetzt.',
      },
      {
        title: 'Branchengerechte Lösungen',
        body: 'Von Finanzen und Immobilien bis Hospitality und Logistik — jedes System richtet sich nach Ihren tatsächlichen Prozessen.',
      },
      {
        title: 'Partner, kein Auftragnehmer',
        body: 'Wir betreuen das System auch nach der Übergabe — Wartung, Upgrades und Support gehören dazu.',
      },
    ],
  },
  services: {
    title: 'Breites Lösungsspektrum aus einer Hand',
    lede: 'Von Finanzen und Immobilien bis Hospitality und Cloud-Infrastruktur — jede Leistung umfasst Umsetzung und langfristige Wartung, ohne Beschränkung auf eine enge Nische.',
    items: {
      crm: {
        title: 'CRM und Anpassungen',
        body: 'Einführung und Anpassung von CRM-Systemen an Ihre Prozesse, Teams und Kunden.',
      },
      erp: {
        title: 'ERP-Systeme und Integrationen',
        body: 'ERP-Anbindung an Vertrieb, Lager und Finanzen sowie Anpassung bestehender Module.',
      },
      processes: {
        title: 'Prozessautomatisierung und -optimierung',
        body: 'Automatisierung interner Abläufe mit Anwendungen, die Entscheidungen beschleunigen und manuelle Arbeit reduzieren.',
      },
      finance: {
        title: 'Finanzlösungen',
        body: 'Systeme für Kostenkontrolle, Abrechnung und Finanzberichte.',
      },
      realEstate: {
        title: 'Immobilien und Asset-Management',
        body: 'Verwaltung von Immobilienportfolios, Mietverträgen und Bewertungen.',
      },
      mobile: {
        title: 'Mobile Anwendungen',
        body: 'Mobile Erweiterungen von Unternehmenssystemen für Außendienst, Lager oder Vertrieb.',
      },
      hospitality: {
        title: 'Hospitality-Lösungen',
        body: 'Systeme für Hotels, Gastronomie und Unterkünfte — PMS-Integrationen, Buchungen, Gästedaten und Betrieb.',
      },
      web: {
        title: 'Weblösungen und Portale',
        body: 'Websites, die zu Ihrem Unternehmenssystem passen.',
      },
      reservations: {
        title: 'Reservierungssysteme',
        body: 'Online-Buchungen in Echtzeit, verbunden mit PMS und Abrechnung.',
      },
      analytics: {
        title: 'Statistiken und Berichte',
        body: 'Dashboards für Entscheidungen auf Basis echter Daten.',
      },
      loyalty: {
        title: 'Kundenbindungssysteme',
        body: 'Treueprogramme mit Punkten, Stufen und Prämien, Mitgliedskarten und einer mobilen App oder Wallet-Karte. Angebote und Kampagnen richten sich nach der Kaufhistorie jedes Mitglieds, und alles bleibt mit Ihrem CRM verbunden.',
      },
      marketing: {
        title: 'Marketing und Newsletter',
        body: 'Newsletter und Kampagnen an Ihre CRM-Kontakte, mit eingebauter Verwaltung von Einwilligungen und Abmeldungen.',
      },
      communication: {
        title: 'Kommunikationslösungen',
        body: 'Maßgeschneiderte Lösungen für die Kommunikation Ihres Teams mit Kunden, eingebunden in Ihr CRM oder eigenständig.',
      },
      warehouse: {
        title: 'Lagerverwaltung',
        body: 'Verfolgung von Lagerbeständen und Warenbewegungen.',
      },
      facility: {
        title: 'Gebäudemanagement',
        body: 'Verwaltung von Objekten und Instandhaltung.',
      },
      api: {
        title: 'API-Integrationen und Middleware',
        body: 'Verbindung von CRM, ERP, PMS und anderen Systemen, die nicht miteinander sprechen.',
      },
      cloud: {
        title: 'Cloud-Infrastruktur und DevOps',
        body: 'Azure-Umgebungen, CI/CD und Automatisierung.',
      },
      microsoft: {
        title: 'Microsoft-Ökosystem',
        body: 'Dynamics 365, Power Platform und Azure, wenn sie für Ihr Unternehmen die richtige Wahl sind.',
      },
      security: {
        title: 'IT-Sicherheit und Compliance',
        body: 'Datenschutz und DSGVO-Konformität.',
      },
      custom: {
        title: 'Individuelle Softwarelösungen',
        body: 'Maßgeschneiderte Lösungen für spezifische Anforderungen.',
      },
      training: {
        title: 'Schulung und Onboarding',
        body: 'Anwenderschulungen und interne Dokumentation.',
      },
      support: {
        title: 'Support und SLA-Verträge',
        body: 'Definierte Reaktions- und Supportzeiten.',
      },
    },
  },
  integrations: {
    title: 'Systeme, die endlich miteinander sprechen',
    lede: 'CRM, ERP und PMS sind selten von selbst verbunden. Wir verknüpfen sie so, dass Daten einmal erfasst und überall sichtbar sind — ohne Abtippen und ohne Tabellen dazwischen.',
    hubLabel: 'Eine zentrale Datenquelle',
    systems: [
      {
        code: 'CRM',
        title: 'Kunden und Vertrieb',
        body: 'Kunden, Verkaufschancen und Kommunikation an einem Ort, synchron mit Angeboten, Verträgen und Abrechnung.',
      },
      {
        code: 'ERP',
        title: 'Finanzen und Betrieb',
        body: 'Rechnungen, Einkauf und Lagerbestand verbunden mit Vertrieb und Lager, ohne doppelte Erfassung.',
      },
      {
        code: 'PMS',
        title: 'Hospitality',
        body: 'Property-Management-Systeme verbunden mit Buchungen, CRM und Buchhaltung.',
      },
      {
        code: 'API',
        title: 'Alles andere',
        body: 'Buchhaltung, Onlineshop, Kassensysteme, Channel-Manager, BI-Tools und interne Anwendungen.',
      },
    ],
    capabilitiesTitle: 'Was Sie bekommen',
    capabilities: [
      'Bidirektionale Datensynchronisation in Echtzeit',
      'Datenmigration ohne Ausfallzeit',
      'Middleware und APIs nach Maß',
      'Monitoring, Protokolle und Warnungen bei Störungen',
    ],
  },
  about: {
    title: 'Über uns',
    statementBefore: 'Ein neues Unternehmen.',
    statementAccent: 'Erfahrung, die nicht bei null beginnt.',
    lede: 'Dalmos Systems wurde als technischer Partner für individuelle Unternehmenssoftware gegründet — Finanzen, Immobilien, Hospitality, Logistik und mehr. Der Ansatz ist die direkte Fortsetzung der Arbeit an großen CRM-Migrations- und Integrationsprojekten in der Immobilien- und Hotelbranche, ohne eine große Organisation dazwischen.',
    processTitle: 'Wie wir arbeiten',
    steps: [
      {
        index: '01',
        title: 'Analyse',
        body: 'Die Prozesse verstehen, bevor die erste Zeile Code entsteht.',
      },
      {
        index: '02',
        title: 'Lösungsdesign',
        body: 'Architektur, die sich Ihren Systemen anpasst, nicht umgekehrt.',
      },
      {
        index: '03',
        title: 'Umsetzung',
        body: 'Iterative Entwicklung mit regelmäßigen Abstimmungen mit Ihnen.',
      },
      { index: '04', title: 'Support', body: 'Wir betreuen das System auch nach dem Go-live.' },
    ],
    valuesTitle: 'Was uns leitet',
    values: [
      {
        title: 'Direkter Zugang',
        body: 'Die Person, die das Projekt führt, ist Ihr Gesprächspartner — ohne Zwischenebenen.',
      },
      {
        title: 'Transparenz',
        body: 'Klarer Umfang, klarer Preis und klare Projektgrenzen vor der ersten Zeile Code.',
      },
      {
        title: 'Langfristigkeit',
        body: 'Wir bauen Systeme, die wir selbst pflegen, keine Projekte, die wir zurücklassen.',
      },
    ],
  },
  contact: {
    title: 'Sprechen wir über Ihr System',
    lede: 'Melden Sie sich mit einer Projektbeschreibung — wir antworten mit einem Vorschlag für den nächsten Schritt.',
    responseNote: 'Wir antworten innerhalb von {days} Arbeitstagen.',
    emailLabel: 'E-Mail',
    phoneLabel: 'Telefon',
    form: {
      name: 'Vor- und Nachname',
      namePlaceholder: 'Anna Muster',
      email: 'E-Mail',
      emailPlaceholder: 'anna@firma.de',
      company: 'Unternehmen',
      companyPlaceholder: 'Firmenname (optional)',
      message: 'Nachricht',
      messagePlaceholder:
        'Beschreiben Sie das Projekt, die eingesetzten Systeme und den gewünschten Zeitrahmen.',
      consent:
        'Ich bin damit einverstanden, dass meine Daten zur Beantwortung dieser Anfrage verwendet werden.',
      submit: 'Anfrage senden',
      submitting: 'Wird gesendet…',
      successTitle: 'Ihre Anfrage wurde gesendet.',
      successBody: 'Wir melden uns unter der angegebenen E-Mail-Adresse bei Ihnen.',
      errorTitle: 'Senden fehlgeschlagen.',
      errorBody: 'Bitte versuchen Sie es erneut oder schreiben Sie uns direkt per E-Mail.',
      fallbackHint: 'E-Mail-Programm öffnen',
      errors: {
        nameRequired: 'Bitte geben Sie Ihren Vor- und Nachnamen ein.',
        emailRequired: 'Bitte geben Sie Ihre E-Mail-Adresse ein.',
        emailInvalid: 'Diese E-Mail-Adresse ist nicht gültig.',
        messageRequired: 'Bitte geben Sie eine Nachricht ein.',
        messageTooShort: 'Die Nachricht muss mindestens 20 Zeichen lang sein.',
        consentRequired: 'Die Einwilligung in die Datenverarbeitung ist erforderlich.',
      },
    },
  },
  footer: {
    tagline:
      'Individuelle Unternehmenssoftware für Finanzen, Immobilien, Hospitality und Logistik.',
    navTitle: 'Navigation',
    legalTitle: 'Rechtliches',
    privacy: 'Datenschutzerklärung',
    legalNotice: 'Impressum',
    rights: 'Alle Rechte vorbehalten.',
    builtIn: 'Ulica Sedmog kontinenta 1, 22000 Šibenik, Kroatien',
  },
  privacy: {
    title: 'Datenschutzerklärung',
    lastUpdatedLabel: 'Letzte Aktualisierung',
    lastUpdated: '2026-10-31',
    intro:
      'Diese Erklärung beschreibt, wie Dalmos Systems die personenbezogenen Daten verarbeitet, die Sie uns über diese Website senden.',
    sections: [
      {
        heading: 'Verantwortlicher',
        paragraphs: [
          'Verantwortlicher ist {controller}. Bei Fragen zur Datenverarbeitung schreiben Sie an {email}.',
        ],
      },
      {
        heading: 'Welche Daten wir erheben',
        paragraphs: [
          'Über das Kontaktformular erheben wir Ihren Namen, Ihre E-Mail-Adresse, den Firmennamen, falls angegeben, und den Inhalt Ihrer Nachricht.',
          'Wir verwenden keine Werbe-Cookies und keine Werkzeuge, die Besucher über andere Websites hinweg verfolgen.',
          'Wir zählen Besuche mit Cloudflare Web Analytics, einem cookielosen Skript von Cloudflare (static.cloudflareinsights.com). Beim Laden einer Seite sendet es anonyme Seitenaufruf- und Leistungsdaten an Cloudflare: die Adresse der Seite, die Seite, von der Sie kamen, sowie Ladezeiten und ähnliche Messwerte zur Leistung der Seite; Browser, Betriebssystem, Gerätetyp und Land ergänzt Cloudflare aus der Anfrage. Das Skript setzt keine Cookies, nutzt keinen anderen Speicher auf Ihrem Gerät und erzeugt keine Besucherkennung. Ihre IP-Adresse erreicht Cloudflare mit der Anfrage, wie bei jeder Website; Cloudflare nutzt sie nicht, um Sie zu identifizieren, und speichert sie dafür nicht. Wir sehen nur Gesamtzahlen, etwa Seitenaufrufe, verweisende Websites, Länder und Gerätetypen, und nutzen sie, um zu verstehen, wie die Website genutzt wird, und um sie schnell zu halten. Rechtsgrundlage ist unser berechtigtes Interesse am Betrieb und an der Verbesserung der Website (Art. 6 Abs. 1 lit. f DSGVO). Cloudflare verarbeitet die Daten für uns als Auftragsverarbeiter nach seinem Auftragsverarbeitungsvertrag mit den EU-Standardvertragsklauseln für Übermittlungen außerhalb der EU. Cloudflare speichert die vollständigen Daten 7 Tage und danach eine Stichprobe von etwa einem Zehntel, aus der wir Statistiken für die letzten sechs Monate sehen. Sie können das Skript in den Einstellungen Ihres Browsers oder mit einem Inhaltsblocker sperren; die Website funktioniert ohne es genauso. Dasselbe gilt für die Medius-Website medius.dalmossystems.com, die auf diese Erklärung verweist.',
        ],
      },
      {
        heading: 'Zweck und Rechtsgrundlage',
        paragraphs: [
          'Wir verwenden die Daten ausschließlich zur Beantwortung Ihrer Anfrage und zur Erstellung eines Angebots. Rechtsgrundlage ist Ihre Einwilligung sowie unser berechtigtes Interesse an der Kommunikation mit potenziellen Kunden.',
        ],
      },
      {
        heading: 'Speicherdauer',
        paragraphs: [
          'Anfragen bewahren wir höchstens vierundzwanzig Monate nach der letzten Kommunikation auf und löschen sie danach, sofern keine längere Aufbewahrung aufgrund vertraglicher oder gesetzlicher Pflichten erforderlich ist.',
        ],
      },
      {
        heading: 'Ihre Rechte',
        paragraphs: [
          'Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit sowie das Recht, Ihre Einwilligung jederzeit zu widerrufen.',
          'Beschwerden können Sie bei der kroatischen Datenschutzbehörde (AZOP) als Aufsichtsbehörde einlegen.',
        ],
      },
      {
        heading: 'Sicherheit und Datenübermittlung',
        paragraphs: [
          'Der Datenverkehr zwischen Ihrem Browser und unseren Servern ist verschlüsselt. Wir verkaufen Ihre Daten nicht und geben sie nicht an Dritte weiter, außer an Dienstleister, die uns beim Betrieb der Website und bei der Kommunikation unterstützen, auf Grundlage eines Auftragsverarbeitungsvertrags.',
        ],
      },
    ],
    backHome: 'Zurück zur Startseite',
  },
  legal: {
    title: 'Impressum',
    intro:
      'Angaben zum Diensteanbieter nach dem kroatischen Gesetz über den elektronischen Geschäftsverkehr.',
    nameLabel: 'Eingetragener Name',
    shortNameLabel: 'Kurzname',
    seatLabel: 'Sitz',
    country: 'Kroatien',
    registerLabel: 'Register',
    registerValue: 'Kroatisches Handwerksregister (Obrtni registar), Gespanschaft Šibenik-Knin',
    mboLabel: 'Registernummer (MBO)',
    registeredOnLabel: 'Eingetragen am',
    activityLabel: 'Haupttätigkeit',
    activityValue:
      'Entwurf, Entwicklung und Wartung von Computerprogrammen und Anwendungen im Kundenauftrag (NKD 2025: 62.10.9)',
    contactLabel: 'Kontakt',
    backHome: 'Zurück zur Startseite',
  },
  mediusPrivacy: {
    ...mediusPrivacyEn,
    lastUpdatedLabel: 'Gültig ab',
    languageNote:
      'Diese Datenschutzerklärung für Medius gibt es auf Englisch und Kroatisch; unten steht die englische Fassung.',
    backHome: 'Zurück zur Startseite',
  },
  mediusDeletion: {
    ...mediusDeletionEn,
    lastUpdatedLabel: 'Gültig ab',
    languageNote:
      'Diese Anleitung gibt es auf Englisch und Kroatisch; unten steht die englische Fassung.',
    backHome: 'Zurück zur Startseite',
  },
  mediusTerms: {
    ...mediusTermsEn,
    lastUpdatedLabel: 'Gültig ab',
    languageNote:
      'Diese Nutzungsbedingungen für Medius gibt es auf Englisch und Kroatisch; unten steht die englische Fassung.',
    backHome: 'Zurück zur Startseite',
  },
  mediusLinks: {
    mediusPrivacy: 'Medius – Datenschutzerklärung',
    mediusDeletion: 'Löschung Ihrer Daten beantragen',
    mediusTerms: 'Medius – Nutzungsbedingungen',
  },
  sparsaPrivacy: {
    ...sparsaPrivacyEn,
    lastUpdatedLabel: 'Gültig ab',
    languageNote:
      'Diese Datenschutzhinweise für Sparsa gibt es auf Englisch und Kroatisch; unten steht die englische Fassung.',
    backHome: 'Zurück zur Startseite',
  },
  sparsaDeletion: {
    ...sparsaDeletionEn,
    lastUpdatedLabel: 'Gültig ab',
    languageNote:
      'Diese Anleitung gibt es auf Englisch und Kroatisch; unten steht die englische Fassung.',
    backHome: 'Zurück zur Startseite',
  },
  sparsaTerms: {
    ...sparsaTermsEn,
    lastUpdatedLabel: 'Gültig ab',
    languageNote:
      'Diese Nutzungsbedingungen für Sparsa gibt es auf Englisch und Kroatisch; unten steht die englische Fassung.',
    backHome: 'Zurück zur Startseite',
  },
  sparsaAcceptableUse: {
    ...sparsaAcceptableUseEn,
    lastUpdatedLabel: 'Gültig ab',
    languageNote:
      'Diese Nutzungsrichtlinie für Sparsa gibt es auf Englisch und Kroatisch; unten steht die englische Fassung.',
    backHome: 'Zurück zur Startseite',
  },
  sparsaLinks: {
    sparsaPrivacy: 'Sparsa – Datenschutzhinweise',
    sparsaDeletion: 'Newsletter abbestellen und Daten löschen',
    sparsaTerms: 'Sparsa – Nutzungsbedingungen',
    sparsaAcceptableUse: 'Sparsa – Nutzungsrichtlinie',
  },
  notFound: {
    code: '404',
    title: 'Seite nicht gefunden',
    body: 'Die aufgerufene Adresse existiert nicht oder wurde verschoben.',
    cta: 'Zurück zur Startseite',
  },
}
